import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { LogIn } from 'lucide-react';

/**
 * GuestSignInPrompt — modal that pops once per browser-session for
 * unauthenticated visitors on the linktree homepage. Surfaces the
 * value of signing in vs continuing as a guest, then either routes to
 * /login or closes silently.
 *
 * Why session-scoped (not localStorage): guests who come back days
 * later may have warmed up to the idea of signing up; we re-prompt
 * once per browser session rather than nagging on every page load
 * (sessionStorage) or never again (localStorage). A small delay before
 * showing the modal keeps it from blocking the initial first impression
 * of the linktree itself.
 */
const SESSION_KEY = 'cca_guest_signup_prompt_shown';
const SHOW_DELAY_MS = 4500;
const API = process.env.REACT_APP_BACKEND_URL;

/**
 * pingPromptEvent — fire-and-forget analytics ping to the shared
 * /api/errors/log endpoint so we can measure prompt conversion rate
 * (signin clicks vs continue-as-guest clicks vs shown-but-dismissed).
 * Reusing the existing error-logger endpoint keeps the backend
 * surface minimal — `kind` discriminates these events from real
 * client errors.
 *
 * Pulled into a module-scoped helper (not a hook) so the click
 * handlers can call it without re-rendering the dialog.
 */
function pingPromptEvent(choice) {
  if (!API) return;
  try {
    fetch(`${API}/api/errors/log`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
      body: JSON.stringify({
        kind: 'guest_prompt_clicked',
        message: choice, // 'signin' | 'continue' | 'shown'
        url: window.location.href.slice(0, 500),
        route: window.location.pathname.slice(0, 200),
        user_agent: navigator.userAgent.slice(0, 400),
      }),
    }).catch(() => { /* silent */ });
  } catch {
    /* never throw from analytics */
  }
}

const GuestSignInPrompt = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useAuth();
  const { isSpanish } = useLanguage();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (isLoading) return;
    if (isAuthenticated) return;
    let shown = false;
    try { shown = sessionStorage.getItem(SESSION_KEY) === '1'; } catch { /* ignore */ }
    if (shown) return;
    const t = setTimeout(() => {
      setOpen(true);
      try { sessionStorage.setItem(SESSION_KEY, '1'); } catch { /* ignore */ }
      pingPromptEvent('shown');
    }, SHOW_DELAY_MS);
    return () => clearTimeout(t);
  }, [isAuthenticated, isLoading]);

  if (isAuthenticated) return null;

  const handleSignIn = () => {
    setOpen(false);
    pingPromptEvent('signin');
    navigate('/login');
  };

  const handleContinue = () => {
    setOpen(false);
    pingPromptEvent('continue');
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        // max-w-lg + tighter px on mobile so both buttons + the full
        // description fit on phone widths without horizontal overflow.
        className="w-[calc(100%-2rem)] max-w-lg p-5 sm:p-6"
        data-testid="guest-signin-prompt"
      >
        <DialogHeader>
          <DialogTitle className="text-center text-lg">
            {isSpanish ? '👋 ¡Bienvenido!' : '👋 Welcome!'}
          </DialogTitle>
          <DialogDescription className="text-center text-sm sm:text-base leading-relaxed pt-2">
            {isSpanish
              ? '¿Te gustaría iniciar sesión (o crear una cuenta gratis) para guardar favoritos, ver recursos, conectarte con amigos, ganar XP y aparecer en la clasificación?'
              : 'Would you like to sign in (or create a free account) to save favorites, view resources, connect with friends, earn XP, & rank on the leaderboard?'}
          </DialogDescription>
        </DialogHeader>
        {/* Buttons: Sign In on LEFT, Continue-as-guest on RIGHT per user
            feedback May 2026. `flex-row` (not the shadcn default
            `flex-col-reverse sm:flex-row`) so the order is honored on
            both mobile and desktop. */}
        <DialogFooter className="flex flex-row flex-wrap gap-2 pt-3 sm:justify-between">
          <Button
            type="button"
            onClick={handleSignIn}
            data-testid="guest-prompt-signin"
            className="flex-auto sm:flex-initial bg-[#00E5CC] text-[#0d1f2d] hover:bg-[#00D9C0] gap-1.5 text-sm px-3"
          >
            <LogIn className="h-4 w-4 flex-shrink-0" />
            <span className="truncate">
              {isSpanish ? 'Iniciar / Crear cuenta' : 'Sign In / Create Account'}
            </span>
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={handleContinue}
            data-testid="guest-prompt-continue"
            className="flex-auto sm:flex-initial text-sm px-3"
          >
            <span className="truncate">
              {isSpanish ? 'Seguir como invitado' : "Continue as guest"}
            </span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default GuestSignInPrompt;
